import React, { createRef, useEffect, useState } from 'react';
import { format } from '@teetsh/app/src/utils/time/format';
import parse from '@teetsh/app/src/utils/time/parse';
import isMatch from '@teetsh/app/src/utils/time/isMatch';
import { AutoSuggest } from '@teetsh/app/src/components/Form/AutoSuggest';
import { getMinutesRanges } from './getMinutesRanges';
import { getDurationInHoursAndMinutesBetween } from '@teetsh/app/src/utils/time';
import { getNearestSlot } from './getNearestSlot';
import { UserLocale } from '@teetsh/app/src/types/user';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';

const TIME_GAP = 5;

export interface Props {
  id: string;
  value: Date | undefined;
  onChange: (v: Date | undefined) => void;
  label?: string;
  min?: Date;
  className?: string;
}

// TODO use localized time format to support AM/PM?
export const timeFormat = 'HH:mm';
const alternativeTimeFormat = "HH'h'mm";

function getStringValueFromDate(locale: UserLocale, date?: Date) {
  return date ? format(date, locale, timeFormat) : '00:00';
}

export default function InputTimePicker(props: Props) {
  const { locale } = useUser();
  const [value, setValue] = useState<string>(
    getStringValueFromDate(locale, props.value),
  );
  useEffect(() => {
    if (getStringValueFromDate(locale, props.value) !== value) {
      setValue(getStringValueFromDate(locale, props.value));
    }
  }, [props.value]);
  const suggestions = getMinutesRanges(locale, TIME_GAP, props.min).map(v => ({
    value: v,
    label: `${v}${
      props.min
        ? ` (${getDurationInHoursAndMinutesBetween(
            props.min,
            parse(v, timeFormat, props.min),
          )})`
        : ''
    }`,
    ref: createRef(),
  }));
  return (
    <AutoSuggest
      id={props.id}
      noFilterOnSuggestions
      className={props.className}
      options={suggestions}
      label={props.label}
      findFct={(suggs, v) => getNearestSlot(suggs, v, TIME_GAP, locale)}
      value={value}
      onChange={v => {
        setValue(v);
      }}
      onBlur={highlightedValue => {
        const v = highlightedValue || value;
        if (isMatch(v, timeFormat)) {
          const newVal = parse(v, timeFormat, props.value);
          return props.onChange(newVal);
        }
        if (isMatch(v, alternativeTimeFormat)) {
          const newVal = parse(v, alternativeTimeFormat, props.value);
          setValue(getStringValueFromDate(locale, newVal));
          return props.onChange(newVal);
        }
        setValue(getStringValueFromDate(locale, props.value));
        return props.onChange(props.value);
      }}
    />
  );
}
